import { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../actions'
import { Icon, IconType } from '@lmc/ui-kit'
import Text from '@lmc/ui-kit/src/general/Typography/Text/Text'
import { isNoticeboardGranted, isRouteSpecificPath } from '../utils'
import { Home, HomeGroup, UserRole, User } from '@lmc/ts-common'
import { getSSOManageAccountLink } from 'utils/sso'
import * as GlobalActions from 'actions/Global'
import { getCarerRole } from 'screens/Organisation/screens/TeamList'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { ProfileItem } from './ProfileItem'
import { NoticeboardItem } from './NoticeboardItem'
import { colors } from '@lmc/common/constants'

const HELP_CENTRE_LINK = '/help'

type Props = {
  user: User & { privacyOfficer?: boolean }
  home: Home
  handleSignOut: () => void
}

export const FrontLinkItem: FC<Props> = ({ user, home, handleSignOut }) => {
  const dispatch = useDispatch()

  const homeId = home.id

  const homeName = home?.name

  const fullName = `${user?.name?.first} ${user?.name?.last}`

  const homeGroup = useSelector(
    (state: { global: { homeGroup: HomeGroup } }) => state?.global?.homeGroup
  )

  const isGroupUser = [UserRole.groupAdmin, UserRole.groupManager].includes(
    user.role
  )

  const settingsUrl = isGroupUser
    ? '/settings/skills-and-needs'
    : '/settings/billing'

  const isSettingsPath = isRouteSpecificPath('/settings')

  const isHelpPath = isRouteSpecificPath('/help')

  const isNoticeboardPath = isRouteSpecificPath('/noticeboard')

  const showNoticeboard = isNoticeboardGranted(home)

  const handleSwitchOfficer = () => {
    dispatch(
      GlobalActions.setGlobalUser({
        ...user,
        privacyOfficer: !user?.privacyOfficer,
      })
    )
  }

  const handleHomeSwitch = (home: string) => {
    dispatch(Actions.openModal({ modalId: 'switchHomeModal' }))
    dispatch(
      Actions.addForm({
        formId: 'switchHomeModal',
        initial: { home },
      })
    )
  }

  const handleManageAccount = () =>
    window.open(getSSOManageAccountLink(), '_blank')

  return (
    <Wrapper id="intro-js-step-navbar">
      {showNoticeboard && (
        <NoticeboardItem isNoticeboardPath={isNoticeboardPath} />
      )}
      <Link
        data-cy="nav-btn-help-centre"
        to={HELP_CENTRE_LINK}
        style={styles.helpCentreLink}
      >
        <HelpCentreWrapper $isHelpPath={isHelpPath}>
          <Icon customIcon={IconType.HelpSSIcon} style={styles.helpIcon} />
        </HelpCentreWrapper>
      </Link>
      {user.role !== UserRole.seniorCarer && (
        <Link
          data-cy="nav-btn-settings"
          to={settingsUrl}
          style={styles.settingsLink}
        >
          <SettingsWrapper $isSettingsPath={isSettingsPath}>
            <Icon customIcon={IconType.Settings} style={styles.settingsIcon} />
          </SettingsWrapper>
        </Link>
      )}
      <InfoStyled>
        <Text weight="bold" level={1}>
          {fullName}
        </Text>
        <Text level={2} data-cy="role-tag">
          {getCarerRole(user.role)} at {homeName || homeGroup?.name}
        </Text>
      </InfoStyled>
      <ProfileItemWrapper>
        <ProfileItem
          handleSignout={handleSignOut}
          handleHomeSwitch={() => handleHomeSwitch(homeId)}
          handleSwitchOfficer={handleSwitchOfficer}
          handleManageAccount={handleManageAccount}
          user={user}
          home={home}
          onHover
        />
      </ProfileItemWrapper>
    </Wrapper>
  )
}

const ProfileItemWrapper = styled.div`
  margin-right: 15px;
  display: flex;
  align-items: center;
`

const Wrapper = styled.div`
  margin-right: 15;
  display: flex;
  align-items: center;
`

const HelpCentreWrapper = styled.div<{ $isHelpPath: boolean }>`
  border-radius: 5px;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) => (props.$isHelpPath ? '#704D94' : 'none')};
  color: ${(props) =>
    props.$isHelpPath ? props.theme.color.neutral.color11 : '#B8A6C9'};
`

const SettingsWrapper = styled.div<{ $isSettingsPath: boolean }>`
  border-radius: 5px;
  margin-left: 10px;
  margin-right: 10px;
  width: 48px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) => (props.$isSettingsPath ? '#704D94' : 'none')};
  color: ${(props) =>
    props.$isSettingsPath ? props.theme.color.neutral.color11 : '#B8A6C9'};
`

const InfoStyled = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.color.neutral.color7};
  margin-right: 15px;
  align-items: end;
`

export const styles = {
  logoutIcon: { color: colors.lightpurple },
  helpCentreLink: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 10,
    textDecoration: 'none',
  },
  helpIcon: {
    width: '26px',
    height: '26px',
    cursor: 'pointer',
    marginTop: 4,
    marginLeft: 2,
  },
  settingsLink: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 10,
    textDecoration: 'none',
  },
  settingsIcon: {
    width: '26px',
    height: '26px',
    cursor: 'pointer',
  },
}
